import { useEffect, useRef, useState } from 'react';
import { SEOHead } from '@/components/seo/SEOHead';

declare global {
  interface Window {
    Cal?: any;
    gtag?: (...args: any[]) => void;
  }
}

// Lien public Cal (fourni)
const CAL_PUBLIC_LINK = "https://cal.com/cosmoswave/rdv-qualification-pro?user=cosmoswave&overlayCalendar=true";
// On en déduit slug + query pour l'inline
const CAL_SLUG = "cosmoswave/rdv-qualification-pro";
const CAL_QUERY = "user=cosmoswave&overlayCalendar=true";

export default function AuditGratuit() {
  const [useIframeFallback, setUseIframeFallback] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    let timeoutId: number | undefined;
    const scriptId = "cal-embed-js";
    if (initializedRef.current) return;

    // Track CTA conversion
    if (window.gtag) {
      window.gtag('event', 'cta_audit_click', {
        event_category: 'engagement',
        event_label: 'audit_gratuit_page'
      });
    }

    function startInline() {
      const Cal = (window as any).Cal;
      if (!Cal) return;
      try {
        Cal("init", { origin: "https://cal.com" });
        Cal("inline", {
          elementOrSelector: "#cal-inline",
          calLink: `${CAL_SLUG}?${CAL_QUERY}`,
          layout: "month_view",
          config: { 
            hideEventTypeDetails: false,
            language: "fr",
            theme: "dark",
            timezone: "Europe/Paris"
          },
        });
        initializedRef.current = true;
      } catch {
        // Ignore errors and let fallback handle it
      }
    }

    // Si le script existe déjà
    if (document.getElementById(scriptId)) {
      startInline();
    } else {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://cal.com/embed.js";
      script.async = true;
      script.onload = () => startInline();
      document.body.appendChild(script);
    }

    // Listen for Cal.com booking success
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://cal.com') return;
      
      if (event.data?.type === 'booking:completed') {
        // Track successful booking
        if (window.gtag) {
          window.gtag('event', 'cal_booking_success', {
            event_category: 'conversion',
            event_label: 'audit_gratuit',
            booking_id: event.data.bookingId
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Fallback automatique si rien n'apparaît sous 2.5s
    timeoutId = window.setTimeout(() => {
      if (!initializedRef.current) setUseIframeFallback(true);
    }, 2500);

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <>
      <SEOHead 
        title="Réserver un audit gratuit"
        description="Bénéficiez d'un diagnostic personnalisé de votre digital shelf Amazon et de recommandations actionnables pour optimiser vos performances."
      />
      
      <main className="min-h-screen bg-gradient-to-b from-background to-background/80">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Réservez votre audit gratuit
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Bénéficiez d'un diagnostic personnalisé de votre digital shelf Amazon et de recommandations actionnables pour optimiser vos performances et maximiser votre ROI.
            </p>
          </div>

          {/* Benefits Section */}
          <div className="bg-card rounded-2xl p-8 mb-12 border border-border/50 shadow-elegant">
            <h2 className="text-2xl font-semibold mb-6 text-center">Ce que vous obtenez</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="font-semibold mb-2">Analyse rapide</h3>
                <p className="text-sm text-muted-foreground">Évaluation de vos listings et de votre visibilité actuelle</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h3 className="font-semibold mb-2">Priorisation ROI</h3>
                <p className="text-sm text-muted-foreground">Identification des optimisations à fort impact business</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="font-semibold mb-2">Feuille de route</h3>
                <p className="text-sm text-muted-foreground">Plan d'action claire et chiffrée pour vos prochaines étapes</p>
              </div>
            </div>
          </div>

          {/* Cal.com Embed Section */}
          <div className="bg-card rounded-2xl border border-border/50 shadow-elegant overflow-hidden">
            <div className="p-6 border-b border-border/50">
              <h2 className="text-xl font-semibold text-center">Choisissez votre créneau</h2>
            </div>

            {!useIframeFallback ? (
              <div
                id="cal-inline"
                className="px-2 md:px-6 py-6"
                style={{ minHeight: '750px', width: '100%' }}
                aria-label="Widget de réservation Cal.com"
              />
            ) : (
              // Fallback iFrame si l'embed ne s'initialise pas
              <div className="px-2 md:px-6 py-6">
                <iframe
                  title="Réservation Cal"
                  src={`${CAL_PUBLIC_LINK}&embed=true&theme=dark&language=fr`}
                  style={{ width: "100%", minHeight: '750px', border: 0 }}
                  allow="clipboard-read; clipboard-write; camera; microphone; geolocation"
                />
              </div>
            )}
          </div>

          {/* Trust Section */}
          <div className="text-center mt-12">
            <p className="text-sm text-muted-foreground mb-4">
              Rejoignez les dizaines d'entreprises qui nous font confiance
            </p>
            <div className="flex justify-center items-center space-x-8 opacity-60">
              <div className="text-xs font-medium">AMAZON ADS</div>
              <div className="text-xs font-medium">SP-API</div>
              <div className="text-xs font-medium">BIGQUERY</div>
              <div className="text-xs font-medium">GA4</div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}